import { Component, EnvironmentInjector, inject, Input, ViewChild } from '@angular/core'
import { ActivatedRoute, ActivatedRouteSnapshot, Route, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { FileCopyEndpointRequest, FileCopyEndpointRequestFoldersEnum, FileCopyEndpointRequestOverwriteEnum, FileMoveEndpointRequest, GetMediaFileContentsRequest, MediaFile, MediaFileBundle, MediaFileContents, MediaRootDetail, MediaRootPermission } from 'elements-sdk'
import delay from 'delay'

import { APIService } from 'api/module'
import { BaseComponent } from 'common/helpers'
import { MessageBoxService, NotifyService, PageTitleService, SocketService } from 'common/util/module'
import { licenseGuard } from 'common/util/services/license.service'
import { FileConflictService } from 'common/util.fileConflict/module'
import { permissionGuard } from 'main/services/identity.service'
import { ParametersService } from 'main/services/parameters.service'
import { UploaderService } from 'main/services/uploader.service'
import { PanelService } from 'panel/module'

import { MediaScannerService } from '../services/mediaScanner.service'
import { ClipboardItem, MediaClipboardService } from '../services/mediaClipboard.service'
import { SubclipsClipboardService } from '../services/subclipsClipboard.service'
import { MediaPrevNextNavigationService } from '../services/mediaPrevNextNavigation.service'
import { CreateEditorProjectModalComponent } from '../components/createEditorProjectModal.component'
import { MediaLibraryViewOptionsService } from '../services/mediaLibraryViewOptions.service'
import { MediaPermissionsService } from '../services/mediaPermissions.service'

import { SubclipsClipboardComponent } from '../components/subclipsClipboard.component'
import { SubtitlesClipboardComponent } from 'media/components/subtitlesClipboard.component'
import { isBundle } from 'media/helpers'

const PAGE_SIZE = 50

interface GetDirectoryContentQueryOptionsArgs {
  directoryId: number,
  page: number,
}

export function getDirectoryContentQueryOptions ({
  directoryId,
  page,
}: GetDirectoryContentQueryOptionsArgs): GetMediaFileContentsRequest {
  const viewOptions = inject(MediaLibraryViewOptionsService)
  const sort = viewOptions.getSort({ id: directoryId })
  const params: GetMediaFileContentsRequest = {
    id: directoryId,
    offset: PAGE_SIZE * (page - 1),
    limit: PAGE_SIZE,
    ordering: `${sort.bundleOrdering},${sort.fileOrdering}`,
    includeModifiedBy: true,
  }
  if (viewOptions.hideUnrecognized) {
    params.excludeUnrecognized = true
  }
  if (!viewOptions.showDeleted) {
    params.excludeDeleted = true
  }
  return params
}

export interface MediaDirectoryContentsPagination {
  page: number,
  pageSize: number,
  total: number,
}

export interface MediaDirectoryContents extends MediaFileContents {
  pagination: MediaDirectoryContentsPagination
}

export async function fetchContents (route: ActivatedRouteSnapshot, isFlat = false): Promise<MediaDirectoryContents> {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const page = parseInt(route.queryParamMap.get('page') || '1')
  const directoryId = parseInt(route.paramMap.get('id')!)
  const sort = inject(MediaLibraryViewOptionsService).getSort({ id: directoryId })
  const api = inject(APIService)
  if (isFlat) {
    const response = await api.mediaLibrary.getAllBundlesInSubtreeRaw({
      file: directoryId.toString(),
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * (page - 1),
      includeModifiedBy: true,
      ordering: sort.bundleOrdering,
    })
    const paginationInfo = api.extractPagination(response)
    const bundles = await response.value()
    return {
      directories: [],
      bundles,
      total: paginationInfo?.total ?? bundles.length,
      pagination: paginationInfo ? {
        page,
        pageSize: PAGE_SIZE,
        total: paginationInfo.total,
      } : {
        page: 1,
        pageSize: bundles.length,
        total: bundles.length,
      },
    }
  } else {
    const params = getDirectoryContentQueryOptions({
      directoryId,
      page,
    })
    const contents = await api.mediaLibrary.getMediaFileContents(params)
    return {
      ...contents,
      pagination: {
        page,
        pageSize: PAGE_SIZE,
        total: contents.total,
      },
    }
  }
}

function makeRoute (component, isFlat: boolean): Route {
  return {
    path: '',
    pathMatch: 'full',
    component,
    resolve: {
      contents: (route: ActivatedRouteSnapshot) =>
        fetchContents(route, isFlat),
      root: (route: ActivatedRouteSnapshot) =>
        inject(APIService).mediaLibrary.getMediaRoot({ id: parseInt(route.paramMap.get('rootId')!) }),
      file: async (route: ActivatedRouteSnapshot) => {
        const api = inject(APIService)
        const mediaPermissions = inject(MediaPermissionsService)
        const router = inject(Router)
        const file = await api.mediaLibrary.getMediaFile({
          id: parseInt(route.paramMap.get('id')!),
          includeEffectiveCustomFields: true,
          includeParents: true,
        })
        const p = mediaPermissions.getPermissionForFile(file)
        if (p?.allowUpload && !p.allowRead) {
          router.navigateByUrl(
            `/media/upload/${route.paramMap.get('rootId')}/${file.id}`,
            { replaceUrl: true },
          )
        }
        return file
      },
      isFlat: () => isFlat,
    },
    canActivate: [
      permissionGuard('media:access'),
      licenseGuard('media'),
    ],
    runGuardsAndResolvers: 'always',
  }
}

@Component({
  selector: 'media-browse-view',
  templateUrl: './mediaBrowseView.component.pug',
  styleUrls: ['./mediaBrowseView.component.scss'],
})
export class MediaBrowseView extends BaseComponent {
  @Input() root: MediaRootDetail
  @Input() file: MediaFile
  @Input() isFlat = false
  @Input() contents: MediaDirectoryContents

  @ViewChild(SubclipsClipboardComponent, { 'static': false }) subclipsClipboardDrawer: SubclipsClipboardComponent|undefined
  @ViewChild(SubtitlesClipboardComponent, { 'static': false }) subtitlesClipboardDrawer: SubtitlesClipboardComponent|undefined

  dirs: MediaFile[] = []
  bundles: MediaFileBundle[] = []
  objects: (MediaFileBundle|MediaFile)[] = []
  pagination: MediaDirectoryContentsPagination
  permission?: MediaRootPermission|null

  constructor (
    private api: APIService,
    private pageTitle: PageTitleService,
    public panel: PanelService,
    private mediaPermissions: MediaPermissionsService,
    private socket: SocketService,
    private uploader: UploaderService,
    private router: Router,
    private mediaScanner: MediaScannerService,
    private parameters: ParametersService,
    private route: ActivatedRoute,
    public subclipsClipboard: SubclipsClipboardService,
    private mediaPrevNextNavigation: MediaPrevNextNavigationService,
    private injector: EnvironmentInjector,
    private messageBox: MessageBoxService,
    public mediaClipboard: MediaClipboardService,
    private translate: TranslateService,
    private fileConflict: FileConflictService,
    private notify: NotifyService,
    private ngbModal: NgbModal,
    mediaLibraryViewOptions: MediaLibraryViewOptionsService,
  ) {
    super()
    this.subs.add(mediaLibraryViewOptions.change$.subscribe(() => {
      this.reload()
    }))
  }

  async ngOnInit () {
    this.contents = this.route.snapshot.data['contents']
    this.file = this.route.snapshot.data['file']
    this.root = this.route.snapshot.data['root']
    this.isFlat = this.route.snapshot.data['isFlat']

    this.dirs = this.contents.directories
    this.bundles = this.contents.bundles
    this.pagination = this.contents.pagination

    this.panel.plugin.currentMediaRootID = this.root.id

    if (this.root.path === this.file.path) {
      this.pageTitle.set(this.root.name)
    } else {
      this.pageTitle.set(this.file.name)
    }

    this.permission = this.mediaPermissions.getPermissionForFile(this.file)

    this.subs.add(this.socket.subject('media:dir-updated').subscribe(data => {
      if (data.id === this.file.id) {
        setTimeout(() => this.reload(), 1000)
      }
    }))

    const reloadAsset = async id => {
      const bundle = this.bundles.find(b => b.asset?.id === id)
      if (bundle) {
        await delay(1000)
        const newBundle = await this.api.mediaLibrary.getMediaFileBundle({ id: bundle.id })
        Object.assign(bundle, newBundle)
      }
    }

    this.subs.add(
      this.socket.subject('media:asset-updated').subscribe(data => reloadAsset(data.id)),
      this.socket.subject('media:asset-thumbnail-generated').subscribe(data => reloadAsset(data.id)),
      this.socket.subject('media:asset-updated-proxies-failed').subscribe(data => reloadAsset(data.id)),
      this.socket.subject('media:asset-merged').subscribe(data => reloadAsset(data.from)),
    )

    this.objects = [
      ...this.dirs,
      ...this.bundles,
    ]

    if (this.mediaPermissions.getPermissionForFile(this.file)?.allowUpload) {
      this.uploader.activate(this.file.fullPath, this.root)
      this.subs.add(
        this.uploader.uploaded$.subscribe(() => {
          this.router.navigateByUrl(this.router.url)
        }),
      )
    }

    if (this.pagination.page === 1) {
      this.api.mediaLibrary.discoverMedia({
        scannerDiscoverEndpointRequest: {
          path: this.file.fullPath,
        },
      }).then(() => {
        this.scan()
      })
    }
  }

  scan () {
    if (this.parameters.values.mediaAutoScan) {
      this.mediaScanner.rescan(this.file.fullPath)
    }
  }

  async reload () {
    if (!this.route.snapshot.paramMap.get('id')) {
      // Already navigated away
      return
    }
    const contents = await this.injector.runInContext(() => fetchContents(this.route.snapshot, this.isFlat))
    this.dirs = contents.directories
    this.bundles = contents.bundles
    this.pagination.total = contents.pagination.total
    this.objects = [...this.dirs, ...this.bundles]
  }

  onPaginationChange () {
    this.router.navigate([], { queryParams: { page: this.pagination.page } })
  }

  onItemOpened (item: MediaFile|MediaFileBundle) {
    if (isBundle(item)) {
      this.mediaPrevNextNavigation.set({
        total: this.pagination.total,
        currentIndex: (this.pagination.page - 1) * this.pagination.pageSize + this.objects.indexOf(item),
        fn: p => {
          return (this.isFlat
            ? this.api.mediaLibrary.getAllBundlesInSubtree({
              ...p,
              file: this.file.id.toString(),
            }).then(r => ({
              directories: [],
              bundles: r,
            }))
            : this.api.mediaLibrary.getMediaFileContents(p)
          ).then(r => {
            const bundles = r.bundles
            // See explanation in MediaPrevNextNavigationService
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            for (const _ of r.directories) {
              bundles.unshift(undefined)
            }
            return bundles
          })
        },
        params: this.injector.runInContext(() => getDirectoryContentQueryOptions({
          directoryId: this.file.id,
          page: this.pagination.page,
        })),
      })
    }
  }

  goUp = () => {
    this.router.navigateByUrl(`/media/browse${this.isFlat ? '/flat' : ''}/${this.root.id}/${this.file.parent}`)
  }

  onIsFlatChange (isFlat) {
    console.log(this.isFlat, isFlat)
    if (isFlat !== this.isFlat) {
      this.isFlat = isFlat
      this.router.navigateByUrl(`/media/browse${isFlat ? '/flat' : ''}/${this.root.id}/${this.file.id}`)
    }
  }

  pasteItems = async () => {
    try {
      await this.messageBox.show({
        text: `Paste ${this.mediaClipboard.getItems().length} item${this.mediaClipboard.getItems().length > 1 ? 's' : ''}?`,
        positive: this.translate.instant('Paste'),
        negative: this.translate.instant('Cancel'),
      })
    } catch {
      return
    }
    const destination = await this.api.storage.getFile({
      path: this.file.fullPath,
    })
    const names = new Map(destination.files!.map(item => [item.name, item]))
    const clipboardFiles = this.mediaClipboard.getItems()

    let resolution: string|null = null
    let applyToAll: boolean|null = null

    const pasteFiles: ClipboardItem[] = []
    const renameFiles: ClipboardItem[] = []

    for (const file of clipboardFiles) {
      if (!file.files.some(item => names.has(item.name))) {
        pasteFiles.push(file)
        continue
      }

      const existingFile = file.files.filter(item => names.has(item.name))[0]
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!applyToAll) {
        const result = await this.fileConflict.show({
          pasteFile: file.files[0],
          existingFile,
        })
        resolution = result.resolution
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-nullish-coalescing
        applyToAll = applyToAll || result.applyToAll
      }

      if (resolution === 'rename') {
        renameFiles.push(file)
      }

      if (resolution === 'overwrite') {
        pasteFiles.push(file)
      }

      if (resolution === 'skip') {
        continue
      }

      if (resolution === 'cancel') {
        return
      }
    }

    this.sendPaste(pasteFiles)
    this.sendPaste(renameFiles, {
      overwrite: FileCopyEndpointRequestOverwriteEnum.Timestamp,
      folders: FileCopyEndpointRequestFoldersEnum.Rename,
    })

    this.mediaClipboard.clear()
    this.notify.info(this.translate.instant('Pasting items...'))
  }

  sendPaste (pasteFiles: ClipboardItem[], options?: Partial<FileCopyEndpointRequest>) {
    for (const action of ['copy', 'cut']) {
      const urlAction = action === 'cut' ? 'move' : 'copy'
      const pasteData: FileMoveEndpointRequest & FileCopyEndpointRequest = {
        input: [],
        destination: this.file.fullPath,
        ...options as any,
      }

      for (const item of pasteFiles) {
        if (item.state === action) {
          for (const file of item.files) {
            pasteData.input.push(file.fullPath)
          }
        }
      }
      if (pasteData.input.length > 0) {
        if (urlAction === 'copy') {
          this.api.storage.copyFiles({
            fileCopyEndpointRequest: pasteData,
          })
        } else {
          this.api.storage.moveFiles({
            fileMoveEndpointRequest: pasteData,
          })
        }
      }
    }
  }

  createEditorProject = () => {
    const modal = this.ngbModal.open(CreateEditorProjectModalComponent)
    modal.componentInstance.file = this.file
    modal.componentInstance.root = this.root
    modal.componentInstance.bundles = this.bundles
    modal.result.catch(() => null)
  }

  createDirectory = async () => {
    const message = await this.messageBox.prompt({ prompt: this.translate.instant('New directory name') }).catch(() => null)
    if (!message?.value) {
      return
    }
    if (message.value.length > 255) {
      this.notify.error(this.translate.instant('Directory name cannot be longer than 255 characters'))
      return
    }

    try {
      await this.api.storage.createFile({
        fileUpdate: {
          parent: this.file.fullPath,
          name: message.value,
        },
      })
    } catch (e) {
      this.notify.error(this.translate.instant('Could not create a directory'), e.data.message)
      return
    }

    this.reload()
  }

  createShowroom = async () => {
    const message = await this.messageBox.prompt({ prompt: this.translate.instant('New showroom name') }).catch(() => null)
    if (!message?.value) {
      return
    }
    if (message.value.length > 255) {
      this.notify.error(this.translate.instant('Showroom name cannot be longer than 255 characters'))
      return
    }

    const data = {
      parent: this.file.fullPath,
      name: message.value,
    }
    try {
      const newFile = await this.api.storage.createFile({ fileUpdate: data })
      const newDirectory = await this.api.mediaLibrary.discoverMedia({
        scannerDiscoverEndpointRequest: {
          path: newFile.path,
        },
      })
      await this.api.mediaLibrary.markMediaDirectoryAsShowroom(newDirectory)
      this.reload()
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant('Could not create a showroom'), error)
    }
  }

  static ROUTES: Route[] = [
    {
      ...makeRoute(MediaBrowseView, true),
      path: 'media/browse/flat/:rootId/:id',
    },
    {
      ...makeRoute(MediaBrowseView, false),
      path: 'media/browse/:rootId/:id',
    },
    {
      ...makeRoute(MediaBrowseView, true),
      path: 'panel/browse/flat/:rootId/:id',
    },
    {
      ...makeRoute(MediaBrowseView, false),
      path: 'panel/media/browse/:rootId/:id',
    },
  ]
}
